import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calculator, TrendingUp, Clock, Euro, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

// 3.30 — SavingsCalculator: interactive ROI tool
// JS-only, no backend, persuasivo specialmente per agenzie
const SavingsCalculator = () => {
  const { t, i18n } = useTranslation();
  const [articles, setArticles] = useState(8);
  const [minutes, setMinutes] = useState(90);
  const [hourlyRate, setHourlyRate] = useState(35);

  // Currency by locale (rough)
  const currency = useMemo(() => {
    if (i18n.language === 'en') return { symbol: '$', label: 'USD' };
    return { symbol: '€', label: 'EUR' };
  }, [i18n.language]);

  // Orchestra reduces optimization time by ~85% (auto meta + brand voice + auto draft)
  const ORCHESTRA_TIME_FACTOR = 0.15; // user keeps 15% of original time for review
  const PROFESSIONAL_PLAN_PRICE = 49; // EUR/month, base reference

  const calc = useMemo(() => {
    const monthlyMinutesOld = articles * minutes;
    const monthlyMinutesNew = articles * minutes * ORCHESTRA_TIME_FACTOR;
    const minutesSaved = monthlyMinutesOld - monthlyMinutesNew;
    const hoursSaved = minutesSaved / 60;
    const moneySaved = hoursSaved * hourlyRate;
    const planCostMonth = PROFESSIONAL_PLAN_PRICE;
    // Weeks to recoup: how many weeks of savings to pay 1 month of plan
    // weekly savings = moneySaved / 4.33
    const weeklySavings = moneySaved / 4.33;
    const weeksToRecoup = weeklySavings > 0 ? Math.max(0.1, planCostMonth / weeklySavings) : 0;
    const roi = planCostMonth > 0 ? Math.round((moneySaved / planCostMonth - 1) * 100) : 0;
    return {
      hoursSaved: Math.round(hoursSaved),
      moneySaved: Math.round(moneySaved),
      weeksToRecoup: weeksToRecoup.toFixed(1),
      roi,
    };
  }, [articles, minutes, hourlyRate]);

  return (
    <section className="py-24 px-6 bg-[#050505] border-t border-white/5 relative overflow-hidden" data-testid="savings-calculator-section">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#0055FF]/8 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#10B981]/6 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-5xl mx-auto relative z-10">
        <div className="text-center mb-12 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0055FF]/10 border border-[#0055FF]/30 mb-6">
            <Calculator className="w-4 h-4 text-[#0055FF]" />
            <span className="text-sm text-[#93c5fd]">{t('savingsCalc.badge')}</span>
          </div>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-medium tracking-tight mb-6">
            {t('savingsCalc.title1')}
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#0055FF] to-[#10B981]">
              {t('savingsCalc.title2')}
            </span>
          </h2>
          <p className="text-lg text-[#A1A1AA] max-w-2xl mx-auto">
            {t('savingsCalc.subtitle')}
          </p>
        </div>

        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] backdrop-blur-sm p-6 sm:p-10 animate-fade-up">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Inputs */}
            <div className="space-y-7">
              <div className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-semibold mb-2">
                {t('savingsCalc.inputsLabel')}
              </div>

              <div>
                <label className="block text-sm text-[#d4d4d8] mb-2 font-medium">
                  {t('savingsCalc.articlesLabel')}
                  <span className="ml-2 text-[#0055FF] font-bold">{articles}</span>
                </label>
                <input
                  type="range"
                  min="1"
                  max="40"
                  value={articles}
                  onChange={(e) => setArticles(parseInt(e.target.value, 10))}
                  className="w-full accent-[#0055FF]"
                  data-testid="calc-articles-input"
                />
                <div className="flex justify-between text-[10px] text-[#71717A] mt-1">
                  <span>1</span>
                  <span>40+</span>
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#d4d4d8] mb-2 font-medium">
                  {t('savingsCalc.minutesLabel')}
                  <span className="ml-2 text-[#0055FF] font-bold">{minutes} min</span>
                </label>
                <input
                  type="range"
                  min="15"
                  max="240"
                  step="5"
                  value={minutes}
                  onChange={(e) => setMinutes(parseInt(e.target.value, 10))}
                  className="w-full accent-[#0055FF]"
                  data-testid="calc-minutes-input"
                />
                <div className="flex justify-between text-[10px] text-[#71717A] mt-1">
                  <span>15</span>
                  <span>240</span>
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#d4d4d8] mb-2 font-medium">
                  {t('savingsCalc.rateLabel')}
                  <span className="ml-2 text-[#0055FF] font-bold">{currency.symbol}{hourlyRate}</span>
                </label>
                <input
                  type="range"
                  min="10"
                  max="150"
                  step="5"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(parseInt(e.target.value, 10))}
                  className="w-full accent-[#0055FF]"
                  data-testid="calc-rate-input"
                />
                <div className="flex justify-between text-[10px] text-[#71717A] mt-1">
                  <span>{currency.symbol}10</span>
                  <span>{currency.symbol}150+</span>
                </div>
              </div>

              <p className="text-xs text-[#71717A] italic pt-2">
                {t('savingsCalc.assumption')}
              </p>
            </div>

            {/* Outputs */}
            <div className="lg:pl-6 lg:border-l lg:border-white/10">
              <div className="text-xs uppercase tracking-[0.2em] text-[#10B981] font-semibold mb-5">
                {t('savingsCalc.outputsLabel')}
              </div>

              <div className="space-y-4">
                <div className="rounded-2xl bg-gradient-to-br from-[#0055FF]/15 to-[#0055FF]/5 border border-[#0055FF]/30 p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Clock className="w-5 h-5 text-[#0055FF]" />
                    <span className="text-xs uppercase tracking-wider text-[#93c5fd] font-semibold">
                      {t('savingsCalc.hoursTitle')}
                    </span>
                  </div>
                  <div className="text-4xl font-['Outfit'] font-medium text-white">
                    {calc.hoursSaved}<span className="text-xl text-[#A1A1AA] ml-2">h/{t('savingsCalc.monthShort')}</span>
                  </div>
                </div>

                <div className="rounded-2xl bg-gradient-to-br from-[#10B981]/15 to-[#10B981]/5 border border-[#10B981]/30 p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Euro className="w-5 h-5 text-[#10B981]" />
                    <span className="text-xs uppercase tracking-wider text-[#6ee7b7] font-semibold">
                      {t('savingsCalc.moneyTitle')}
                    </span>
                  </div>
                  <div className="text-4xl font-['Outfit'] font-medium text-white">
                    {currency.symbol}{calc.moneySaved}<span className="text-xl text-[#A1A1AA] ml-2">/{t('savingsCalc.monthShort')}</span>
                  </div>
                </div>

                <div className="rounded-2xl bg-gradient-to-br from-[#8B5CF6]/15 to-[#8B5CF6]/5 border border-[#8B5CF6]/30 p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <TrendingUp className="w-5 h-5 text-[#8B5CF6]" />
                    <span className="text-xs uppercase tracking-wider text-[#c4b5fd] font-semibold">
                      {t('savingsCalc.recoupTitle')}
                    </span>
                  </div>
                  <div className="text-3xl font-['Outfit'] font-medium text-white">
                    {calc.weeksToRecoup} <span className="text-base text-[#A1A1AA]">{t('savingsCalc.weeks')}</span>
                  </div>
                  <div className="text-xs text-[#A1A1AA] mt-1">
                    {t('savingsCalc.roiLabel')} <span className="text-[#10B981] font-semibold">+{calc.roi}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-white/10 text-center">
            <Link to="/register">
              <button className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-[#0055FF] hover:bg-[#3377FF] text-white font-medium transition-colors">
                {t('savingsCalc.ctaButton')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <p className="mt-3 text-xs text-[#71717A]">
              {t('savingsCalc.ctaNote')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SavingsCalculator;
